/**
 * Polls the per-patient pending ingestion-review badge count.
 *
 * Backs the Care Delivery AI notification icon. The backend count query is
 * per-patient now and global-ready (Phase 8); this hook fetches it on patient
 * change and exposes a `refresh` so the badge can be re-pulled after a review
 * is opened. The count is non-PHI metadata.
 */
import { useCallback, useEffect, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import type { PendingReviews } from "../types";

/** The result surface returned by {@link usePendingReviews}. */
export interface UsePendingReviewsResult {
  /** The number of pending ingestion reviews for the patient. */
  count: number;
  /** Re-fetch the count. */
  refresh: () => void;
}

const PENDING_ENDPOINT = "/api/blueprint/pending-reviews";

/**
 * Fetch the pending-review count for a patient.
 *
 * @param patientId - The active patient id.
 * @returns The current count and a refresh trigger.
 */
export function usePendingReviews(patientId: string): UsePendingReviewsResult {
  const [count, setCount] = useState(0);
  const [token, setToken] = useState(0);

  const refresh = useCallback(() => setToken((t) => t + 1), []);

  useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      const resp = await apiUtils.fetchData<PendingReviews>(
        `${PENDING_ENDPOINT}?patient_id=${encodeURIComponent(patientId)}`,
      );
      if (!active) return;
      if (isApiError(resp)) {
        setCount(0);
        return;
      }
      setCount(resp.count);
    }

    load().catch((err: unknown) => {
      if (!active) return;
      logger.devOnly.warn("usePendingReviews: load failed", {
        error: String(err),
      });
      setCount(0);
    });

    return () => {
      active = false;
    };
  }, [patientId, token]);

  return { count, refresh };
}
